import { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import styled from '@mui/material/styles/styled';
import { values } from 'lodash';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';

import { LAYOUT_WIDTH } from './constants';
import { useServer } from '../../contexts/ServerContext';
import { useSolrInstance } from '../../contexts/SolrContext';
import { useUser } from '../../contexts/UserContext';
import useLocalStorage from '../../hooks/useLocalStorage';
import { PRIVATE_ROUTE } from '../../routes/routes';
import PrivateHeader from '../sections/header/PrivateHeader';
import { SIDEBAR_TYPE as TYPE } from '../sections/sidebar/constants';
import Sidebar from '../sections/sidebar/Sidebar';

const Offset = styled('div')(({ theme }) => theme.mixins.toolbar);

const StyledMain = styled('main')(({ open, hasSidebar, theme }) => ({
  maxWidth: !hasSidebar
    ? '100%'
    : open
      ? `calc(100% - ${LAYOUT_WIDTH.MAX}px)`
      : `calc(100% - ${LAYOUT_WIDTH.MIN}px)`,
  marginLeft: !hasSidebar ? 0 : open ? LAYOUT_WIDTH.MAX : LAYOUT_WIDTH.MIN,
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.shorter,
  }),
}));

const MainContent = ({ children, open, hasSidebar }) => {
  return (
    <StyledMain open={open} hasSidebar={hasSidebar}>
      <Offset />
      <Box sx={{ pb: 3 }}>{children}</Box>
    </StyledMain>
  );
};

const Layout = ({ sidebar, requireAdmin, ...restProps }) => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { user } = useUser();
  const { instance } = useSolrInstance();
  const { server } = useServer();

  const [open, setOpen] = useLocalStorage('sidebar:open', true);
  const [isAdmin, setIsAdmin] = useState({});

  const handleToggle = () => setOpen((prevOpen) => !prevOpen);

  useEffect(() => {
    if (!user) return;
    const isAdmin = user.isAdmin(projectId);
    if (requireAdmin && !isAdmin) {
      navigate(PRIVATE_ROUTE.ERROR403, { replace: true });
    }
    setIsAdmin({ isAdmin });
  }, [navigate, projectId, requireAdmin, user]);

  return (
    <>
      <PrivateHeader open={!sidebar || open} {...isAdmin} {...restProps} />
      {sidebar && (
        <Sidebar
          open={open}
          type={sidebar}
          handleToggle={handleToggle}
          instance={instance}
          server={server}
          {...isAdmin}
        />
      )}

      <MainContent hasSidebar={!!sidebar} open={open} {...restProps} />
    </>
  );
};

Layout.propTypes = {
  sidebar: PropTypes.oneOf(values(TYPE)),
  requireAdmin: PropTypes.bool,
};

MainContent.propTypes = {
  children: PropTypes.node,
  open: PropTypes.bool,
  hasSidebar: PropTypes.bool,
};

export default Layout;
